import React, { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, Phone, ChevronDown } from "lucide-react";
import { LogoMark } from "../Logo";

const navLinks = [
  { to: "/", label: "Ana Sayfa" },
  {
    label: "Kurumsal",
    children: [
      { to: "/hakkimizda", label: "Hakkımızda" },
      { to: "/hesap-numaralari", label: "Hesap Numaralarımız" },
      { to: "/sirket-profili", label: "Şirket Profili" },
      { to: "/misyon-vizyon", label: "Misyon & Vizyon" },
    ],
  },
  { to: "/cevirici", label: "Döviz Çevirici" },
  { to: "/hizmetler", label: "Hizmetlerimiz" },
  { to: "/haberler", label: "Haberler" },
  { to: "/blog", label: "Blog" },
  { to: "/iletisim", label: "İletişim" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [openSub, setOpenSub] = useState(false);

  return (
    <header
      className="sticky top-0 z-50 glass border-b border-slate-200/60"
      data-testid="site-header"
    >
      <div className="w-full px-5 lg:px-10 xl:px-14">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo">
            <LogoMark size={44} className="group-hover:scale-105 transition-transform shrink-0" />
            <div className="leading-tight">
              <div className="font-display font-bold text-lg md:text-xl text-slate-950 tracking-tight">
                Akaydın <span className="text-brand-gold">Döviz</span>
              </div>
              <div className="text-[9px] md:text-[10px] uppercase tracking-[0.18em] text-slate-500 font-medium mt-0.5">
                Altın Ticareti Sınırlı Yetkili Müessese A.Ş.
              </div>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) =>
              link.children ? (
                <div
                  key={link.label}
                  className="relative group"
                  onMouseEnter={() => setOpenSub(true)}
                  onMouseLeave={() => setOpenSub(false)}
                >
                  <button
                    className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-slate-700 hover:text-slate-950 transition-colors"
                    data-testid="nav-kurumsal"
                  >
                    {link.label}
                    <ChevronDown className="w-3.5 h-3.5" />
                  </button>
                  {openSub && (
                    <div className="absolute top-full left-0 pt-2 min-w-[220px]">
                      <div className="bg-white rounded-lg shadow-xl border border-slate-200 py-2">
                        {link.children.map((c) => (
                          <NavLink
                            key={c.to}
                            to={c.to}
                            className={({ isActive }) =>
                              `block px-4 py-2.5 text-sm transition-colors ${
                                isActive
                                  ? "text-brand-gold bg-slate-50"
                                  : "text-slate-700 hover:text-slate-950 hover:bg-slate-50"
                              }`
                            }
                          >
                            {c.label}
                          </NavLink>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <NavLink
                  key={link.to}
                  to={link.to}
                  data-testid={`nav-${link.to.replace("/", "") || "home"}`}
                  className={({ isActive }) =>
                    `relative px-4 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "text-slate-950"
                        : "text-slate-600 hover:text-slate-950"
                    }`
                  }
                >
                  {({ isActive }) => (
                    <>
                      {link.label}
                      {isActive && (
                        <span className="absolute bottom-0 left-4 right-4 h-0.5 bg-brand-gold rounded-full" />
                      )}
                    </>
                  )}
                </NavLink>
              ),
            )}
          </nav>

          {/* CTA */}
          <div className="hidden lg:flex items-center gap-2">
            <a
              href="tel:+902268112950"
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-slate-950 hover:text-brand-gold transition-colors"
              data-testid="header-phone"
            >
              <Phone className="w-4 h-4" />
              0226 811 29 50
            </a>
            <Link
              to="/cevirici"
              className="px-5 py-2.5 rounded-full bg-slate-950 text-white text-sm font-semibold hover:bg-brand-gold transition-all shadow-sm hover:shadow-md"
              data-testid="header-cta-converter"
            >
              Döviz Çevir
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            className="lg:hidden p-2 -mr-2 text-slate-950"
            onClick={() => setOpen(!open)}
            aria-label="Menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden pb-4 border-t border-slate-200 mt-2 -mx-5 px-5 pt-3" data-testid="mobile-menu">
            <div className="flex flex-col gap-1">
              {navLinks.map((link) =>
                link.children ? (
                  <details key={link.label} className="group">
                    <summary className="flex items-center justify-between px-3 py-2.5 text-sm font-medium text-slate-700 cursor-pointer">
                      {link.label}
                      <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
                    </summary>
                    <div className="pl-4">
                      {link.children.map((c) => (
                        <NavLink
                          key={c.to}
                          to={c.to}
                          onClick={() => setOpen(false)}
                          className="block px-3 py-2 text-sm text-slate-600 hover:text-brand-gold"
                        >
                          {c.label}
                        </NavLink>
                      ))}
                    </div>
                  </details>
                ) : (
                  <NavLink
                    key={link.to}
                    to={link.to}
                    onClick={() => setOpen(false)}
                    className="px-3 py-2.5 text-sm font-medium text-slate-700 hover:text-brand-gold"
                  >
                    {link.label}
                  </NavLink>
                ),
              )}
              <a
                href="tel:+902268112950"
                className="mt-2 flex items-center justify-center gap-2 px-4 py-3 rounded-full bg-slate-950 text-white text-sm font-semibold"
              >
                <Phone className="w-4 h-4" /> 0226 811 29 50
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
